import React, { useState, useEffect } from 'react';
import { ArrowLeft, Rocket, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  resourcesData, 
  useUserCloud, 
  EmailCaptureModal,
  PromptBlock, 
  EmergentCTA,
  AboutEmergent,
  RelatedResources, 
  AuthorCard,
  resourceColors as colors 
} from '../ResourceComponents';

const resource = resourcesData['getting-started'];

const GettingStartedPage = () => {
  const { userEmail, saveUser, saveResource } = useUserCloud();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (userEmail) {
      setHasAccess(true);
      saveResource(resource.id);
    }
  }, [userEmail]);

  const handleSaveToCloud = async (email) => {
    saveUser(email);
    saveResource(resource.id);
    setShowEmailModal(false);
    setHasAccess(true);
  };

  if (!hasAccess && !userEmail) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center">
          <div 
            className="w-24 h-24 rounded-3xl mx-auto mb-6 flex items-center justify-center"
            style={{ background: `${resource.color}20` }}
          >
            <Rocket className="w-12 h-12" style={{ color: resource.color }} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-gray-600 mb-8">{resource.description}</p>
          
          <button
            onClick={() => setShowEmailModal(true)}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-white transition-all hover:opacity-90"
            style={{ background: colors.teal }}
          >
            <Sparkles className="w-5 h-5" />
            Save to Your Cloud
          </button>
          
          <p className="text-gray-400 text-sm mt-4">
            Enter your email to access this resource
          </p>

          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mt-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Data Dreaming
          </Link>
        </div>

        <EmailCaptureModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSubmit={handleSaveToCloud}
          resourceTitle={resource.title}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Resource by</span>
            <span className="text-sm font-medium text-gray-900">{resource.author.handle}</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div 
        className="py-16 px-4"
        style={{ background: `linear-gradient(180deg, ${resource.color}10 0%, transparent 100%)` }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <span 
              className="px-3 py-1 rounded-full text-xs font-medium"
              style={{ background: `${resource.color}20`, color: resource.color }}
            >
              {resource.platform} Prompt
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-xl text-gray-600 max-w-2xl">{resource.description}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Before You Start */}
        <div className="mb-12 p-6 rounded-2xl border-2" style={{ borderColor: resource.color, background: `${resource.color}08` }}>
          <h3 className="font-bold text-gray-900 mb-4">Before You Start Checklist</h3>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            {[
              'Choose your domain name (yourname.com)',
              'Pick a domain registrar (GoDaddy, Namecheap, Cloudflare)',
              'Have a profile photo ready (square, high quality)',
              'List your social links (Instagram, LinkedIn, etc.)',
              'Write your bio/tagline (keep it short)'
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-5 h-5 rounded border border-gray-300 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Prompts */}
        <h3 className="text-2xl font-bold text-gray-900 mb-6">The Prompts</h3>

        <PromptBlock
          title="Personal Brand Website Foundation"
          description="The complete starter prompt. Fill in the brackets with your info."
          prompt={`Build me a personal brand website with the following sections:

1. Hero Section
   - My name/brand name: [YOUR NAME]
   - Tagline: [YOUR TAGLINE]
   - Profile photo
   - Social links (Instagram, LinkedIn, YouTube, Email)
   - Location badge

2. About/What I Do
   - Brief intro (2-3 sentences about who I am)
   - Skills/expertise as visual tags or pills
   - Industries I work in

3. Portfolio/Work Section
   - [CHOOSE ONE: Grid of projects / Video embeds / Case studies]

4. Social Proof
   - Logos of companies I've worked with OR
   - Testimonials OR
   - "Featured in" press mentions

5. Call-to-Action
   - Primary CTA: [e.g., "Work with me" button → link to contact form]
   - Secondary CTA: [e.g., "Join my newsletter"]

6. Footer
   - Social links repeated
   - Copyright

Design style: [CHOOSE: minimal / bold / playful / professional / futuristic]
Color palette: [CHOOSE: dark mode / light mode / specific colors]
Mobile-first: Yes`}
        />

        <EmergentCTA />

        <PromptBlock
          title="Quick Single-Page Portfolio"
          description="Simpler version for a fast launch."
          prompt={`Create a simple one-page portfolio website for a [YOUR PROFESSION].

Include:
- Hero with name, title, and one-liner
- 3-6 portfolio items with images and brief descriptions
- Contact section with email link
- Clean, minimal design
- Smooth scroll navigation

My info:
- Name: [YOUR NAME]
- Title: [YOUR TITLE]
- One-liner: [YOUR TAGLINE]
- Email: [YOUR EMAIL]`}
        />

        <PromptBlock
          title="Link in Bio Page"
          description="Like Linktree but on your own domain."
          prompt={`Create a links page like Linktree but on my own domain at /links

Include:
- Profile photo at top
- My name and short bio
- Styled link buttons for:
  - [LINK 1: e.g., "Latest YouTube Video" → URL]
  - [LINK 2: e.g., "Book a Call" → Calendly URL]
  - [LINK 3: e.g., "Newsletter" → Beehiiv URL]
  - [LINK 4: e.g., "Shop" → Store URL]
- Social icons at bottom
- Matches my website's design style
- Mobile-optimized`}
        />

        <AuthorCard author={resource.author} />
        
        <RelatedResources resourceIds={resource.relatedResources} />

        <AboutEmergent />
      </div>
    </div>
  );
};

export default GettingStartedPage;
